import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts';
import { useDispatch } from 'react-redux';
import {
  setTopNavbar,
  clearTopNavbar,
} from '../../Reducers/actions/navbarActions';
import { getCreatorAnalytics } from '../../Reducers/actions/accountActions';
import { CreatorAnalyticsDashboardSkeleton } from '../../components/Skeletons/BatchScreenSkeletons';
import CreationsAnalytics from './CreationsAnalytics';
import NewsAnalytics from './NewsAnalytics';
import { fromCentsToPounds } from '../../../helpers/money';
import './CreatorAnalytics.scss';

const TABS = [
  { id: 'overall', label: 'OVERALL' },
  { id: 'creations', label: 'CREATIONS' },
  { id: 'news', label: 'NEWS' },
];

/** Earnings per source colors: #FFE8F8, #E865C0, #D3F2FF, #8CDCFF */
const EARNINGS_CHART_COLORS = {
  Physical: '#8CDCFF',
  Digital: '#D3F2FF',
  Monthly: '#E865C0',
  'One-time': '#FFE8F8',
};

/** Paid vs Free: #D3F2FF, #FF8CDC */
const PAID_FREE_COLORS = ['#D3F2FF', '#FF8CDC'];

/**
 * Format Y-axis tick for earnings (0, 5k, 10k, 20k)
 */
function formatYAxisTick(value) {
  if (value >= 1000) {
    return `${value / 1000}k`;
  }
  return String(value);
}

/**
 * Format tooltip value as euros (API returns amounts in cents)
 */
function formatEarningsTooltip(value) {
  if (value == null || value === undefined) return '€0';
  return fromCentsToPounds(Math.round(Number(value)));
}

const DEFAULT_STATS = { creations: 0, news: 0, contributions: 0, purchases: 0 };
const DEFAULT_EARNINGS_CHANGE = { amount: 0, percent: 0 };

function CreatorAnalytics() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth?.user?.user_id);

  const [activeTab, setActiveTab] = useState('overall');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [earningsTotal, setEarningsTotal] = useState(0);
  const [earningsChange, setEarningsChange] = useState(DEFAULT_EARNINGS_CHANGE);
  const [earningsChartData, setEarningsChartData] = useState([]);
  const [paidFreeData, setPaidFreeData] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCreatorAnalytics();
      if (response?.success && response?.data) {
        const d = response.data;
        setStats({
          creations: d.creationsCount ?? 0,
          news: d.newsCount ?? 0,
          contributions: d.contributionsCount ?? 0,
          purchases: d.purchasesCount ?? 0,
        });
        setEarningsTotal(d.earningsTotal ?? 0);
        setEarningsChange(d.earningsChange ?? DEFAULT_EARNINGS_CHANGE);
        const rawChartData = d.earningsChartData ?? [];
        setEarningsChartData(
          rawChartData.map((row) => ({
            date: row.date,
            Physical: row.Physical ?? 0,
            Digital: row.Digital ?? 0,
            Monthly: row.Monthly ?? 0,
            'One-time': row['One-time'] ?? 0,
          }))
        );
        setPaidFreeData(d.paidFreeData ?? []);
      }
    } catch (err) {
      if (err?.status === 403) {
        navigate('/my-profile');
        return;
      }
      const message =
        err?.message?.includes('HTML')
          ? 'Analytics API unavailable. Ensure the backend is running and the endpoint exists.'
          : (err?.message ?? 'Failed to load analytics');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(
      setTopNavbar({
        title: 'ANALYTICS',
        showBackArrow: true,
      })
    );
    fetchAnalytics();
    return () => {
      dispatch(clearTopNavbar());
    };
  }, [dispatch, fetchAnalytics]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const handleTabKeyDown = (e, tabId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTabClick(tabId);
    }
  };

  if (loading) {
    return <CreatorAnalyticsDashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="creator-analytics creator-analytics__content p-16">
        <p className="creator-analytics__error">{error}</p>
      </div>
    );
  }

  return (
    <div
      className="creator-analytics"
      role="main"
      aria-label="Creator analytics dashboard"
    >
      <div className="creator-analytics__content p-16">
        {/* Tabs: OVERALL | CREATIONS | NEWS */}
        <nav
          className="creator-analytics__tabs"
          role="tablist"
          aria-label="Analytics sections"
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              className={`creator-analytics__tab ${
                activeTab === tab.id ? 'creator-analytics__tab--active' : ''
              }`}
              onClick={() => handleTabClick(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === 'overall' && (
          <div
            id="panel-overall"
            role="tabpanel"
            aria-labelledby="tab-overall"
            className="creator-analytics__panel"
          >
            {/* 2x2 Stat cards */}
            <div className="creator-analytics__stats-grid">
              <div className="creator-analytics__stat-card">
                <p className="creator-analytics__stat-label">Creations</p>
                <p className="creator-analytics__stat-value">{stats.creations}</p>
              </div>
              <div className="creator-analytics__stat-card">
                <p className="creator-analytics__stat-label">News</p>
                <p className="creator-analytics__stat-value">{stats.news}</p>
              </div>
              <div className="creator-analytics__stat-card">
                <p className="creator-analytics__stat-label">Contributions</p>
                <p className="creator-analytics__stat-value">
                  {stats.contributions}
                </p>
              </div>
              <div className="creator-analytics__stat-card">
                <p className="creator-analytics__stat-label">Purchases</p>
                <p className="creator-analytics__stat-value">
                  {stats.purchases}
                </p>
              </div>
            </div>

            {/* Earnings per source */}
            <section
              className="creator-analytics__chart-card"
              aria-labelledby="earnings-chart-title"
            >
              <h2 id="earnings-chart-title" className="creator-analytics__chart-title">
                Earnings per source
              </h2>
              <div className="creator-analytics__earnings-header">
                <span className="creator-analytics__earnings-total">
                  {fromCentsToPounds(earningsTotal)}
                </span>
                <span className={`creator-analytics__earnings-change ${earningsChange.amount < 0 ? 'creator-analytics__earnings-change--negative' : ''}`}>
                  {earningsChange.amount >= 0 ? '+' : '-'}{fromCentsToPounds(Math.abs(earningsChange.amount))} ({earningsChange.percent >= 0 ? '+' : ''}{earningsChange.percent}%)
                </span>
                {earningsChartData.length > 0 && (
                  <p className="creator-analytics__chart-subtitle">
                    Here are your earnings for the last {earningsChartData.length} {earningsChartData.length === 1 ? 'day' : 'days'}
                  </p>
                )}
              </div>
              <div className="creator-analytics__stacked-bar">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={earningsChartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" vertical={true} horizontal={true} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#666' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#666' }}
                      tickFormatter={formatYAxisTick}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value) => formatEarningsTooltip(value)}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="Physical" stackId="a" fill={EARNINGS_CHART_COLORS.Physical} name="Physical" />
                    <Bar dataKey="Digital" stackId="a" fill={EARNINGS_CHART_COLORS.Digital} name="Digital" />
                    <Bar dataKey="Monthly" stackId="a" fill={EARNINGS_CHART_COLORS.Monthly} name="Monthly" />
                    <Bar dataKey="One-time" stackId="a" fill={EARNINGS_CHART_COLORS['One-time']} name="One-time" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="creator-analytics__legend" role="list">
                {Object.entries(EARNINGS_CHART_COLORS).map(([name, color]) => (
                  <div
                    key={name}
                    className="creator-analytics__legend-item"
                    role="listitem"
                  >
                    <span
                      className="creator-analytics__legend-dot"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                    <span>{name}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Paid creations vs Free creations */}
            <section
              className="creator-analytics__chart-card"
              aria-labelledby="paid-free-chart-title"
            >
              <h2 id="paid-free-chart-title" className="creator-analytics__chart-title">
                Paid creations vs Free creations
              </h2>
              <div className="creator-analytics__pie-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <RechartsPieChart>
                    <Pie
                      data={paidFreeData}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {paidFreeData.map((entry, index) => (
                        <Cell key={entry.name} fill={PAID_FREE_COLORS[index % 2]} />
                      ))}
                    </Pie>
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="creator-analytics__legend" role="list">
                {paidFreeData.map((entry, index) => (
                  <div
                    key={entry.name}
                    className="creator-analytics__legend-item"
                    role="listitem"
                  >
                    <span
                      className="creator-analytics__legend-dot"
                      style={{ backgroundColor: PAID_FREE_COLORS[index] }}
                      aria-hidden
                    />
                    <span>{entry.displayName}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'creations' && (
          <div className="creator-analytics__panel">
            <CreationsAnalytics userId={userId} />
          </div>
        )}

        {activeTab === 'news' && (
          <div className="creator-analytics__panel">
            <NewsAnalytics userId={userId} />
          </div>
        )}
      </div>
    </div>
  );
}

export default CreatorAnalytics;
